import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { SearchLg } from "@untitledui/icons";
import type { Key } from "react-aria-components";
import { ListBox } from "react-aria-components";
import { Input } from "@/components/base/input/input";
import { BadgeWithButton } from "@/components/base/badges/badges";
import { ModalOverlay as UntitledModalOverlay, Modal as UntitledModal, Dialog as UntitledDialog } from "@/components/application/modals/modal";
import { useCommandMenu } from "./use-command-menu";
import { parseShortcut, getCurrentUserType, getAvailableShortcuts } from "./command-menu-search";
import { CommandMenuItem } from "./command-menu-item";
import { useTranslation } from "react-i18next";
import {
    useCommandPaletteSearch,
    type CommandPaletteItem,
    type CommandPaletteSection,
} from "./use-command-palette-search";

interface CommandMenuProps {
    isOpen: boolean;
    onClose: () => void;
}

export const CommandMenu = ({ isOpen, onClose }: CommandMenuProps) => {
    const { t } = useTranslation(["common"]);
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);
    const listBoxRef = useRef<HTMLDivElement>(null);
    const { selectedEntity, setSelectedEntity } = useCommandMenu();
    const userType = getCurrentUserType();
    const allSelectableItems = useRef<CommandPaletteItem[]>([]);

    const { groups, flatSelectableItems, isLoading } = useCommandPaletteSearch(query, isOpen, selectedEntity);

    useEffect(() => {
        allSelectableItems.current = flatSelectableItems;
    }, [flatSelectableItems]);

    const entityLabels: Record<string, string> = {
        container: t("commandMenu.entities.container"),
        sender: t("commandMenu.entities.sender"),
        receiver: t("commandMenu.entities.receiver"),
        account: t("commandMenu.entities.account"),
        address: t("commandMenu.entities.address"),
        document: t("commandMenu.entities.document"),
    };

    const sectionHeading = useCallback(
        (key: CommandPaletteSection) => {
            const k = `commandMenu.sections.${key}` as const;
            return t(k);
        },
        [t],
    );

    // Parse shortcut quando cambia la query
    useEffect(() => {
        if (query.trim()) {
            const parsed = parseShortcut(query);
            if (parsed.entity && parsed.entity !== selectedEntity) {
                if (userType === "ADMIN" && parsed.entity !== "account") {
                    return;
                }
                setSelectedEntity(parsed.entity);
            }
        }
    }, [query, selectedEntity, setSelectedEntity, userType]);

    // Focus sull'input quando si apre, reset completo quando si chiude
    useEffect(() => {
        if (isOpen && inputRef.current) {
            if (userType === "ADMIN" && selectedEntity && selectedEntity !== "account") {
                setSelectedEntity(null);
            }
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        } else {
            setQuery("");
            setSelectedEntity(null);
            if (inputRef.current) {
                inputRef.current.blur();
            }
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }
        }
    }, [isOpen, setSelectedEntity, userType, selectedEntity]);

    // Navigazione unificata con frecce
    useEffect(() => {
        if (!isOpen) return;

        const handleGlobalKeyDown = (e: KeyboardEvent) => {
            const activeElement = document.activeElement;
            const isInInput =
                activeElement === inputRef.current ||
                (activeElement instanceof HTMLElement && activeElement.closest("input") === inputRef.current);

            if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                if (isInInput) {
                    if (e.key === "ArrowDown") {
                        e.preventDefault();
                        e.stopPropagation();
                        const firstItem = listBoxRef.current?.querySelector('[role="option"]') as HTMLElement;
                        if (firstItem) {
                            firstItem.focus();
                            firstItem.scrollIntoView({ block: "nearest" });
                        }
                    }
                    return;
                }

                const isInList = activeElement?.closest('[role="listbox"]') || activeElement?.closest('[role="option"]');

                if (!isInList) return;

                e.preventDefault();
                e.stopPropagation();

                const allItems = Array.from(
                    listBoxRef.current?.querySelectorAll('[role="option"]') || [],
                ) as HTMLElement[];

                if (allItems.length === 0) return;

                let currentIndex = -1;
                const currentElement = activeElement as HTMLElement;

                for (let i = 0; i < allItems.length; i++) {
                    const item = allItems[i];
                    if (
                        item === currentElement ||
                        item.contains(currentElement) ||
                        (currentElement.closest('[role="option"]') &&
                            item.closest('[role="option"]') === currentElement.closest('[role="option"]'))
                    ) {
                        currentIndex = i;
                        break;
                    }
                }

                if (currentIndex === -1) return;

                if (e.key === "ArrowUp") {
                    if (currentIndex > 0) {
                        allItems[currentIndex - 1].focus();
                        allItems[currentIndex - 1].scrollIntoView({ block: "nearest" });
                    } else {
                        inputRef.current?.focus();
                    }
                } else if (e.key === "ArrowDown") {
                    if (currentIndex < allItems.length - 1) {
                        allItems[currentIndex + 1].focus();
                        allItems[currentIndex + 1].scrollIntoView({ block: "nearest" });
                    }
                }
            }
        };

        window.addEventListener("keydown", handleGlobalKeyDown, true);
        return () => {
            window.removeEventListener("keydown", handleGlobalKeyDown, true);
        };
    }, [isOpen]);

    const handleSelection = useCallback(
        (key: Key) => {
            const keyString = String(key);
            const activeElement = document.activeElement as HTMLElement;
            if (activeElement) {
                activeElement.blur();
            }

            onClose();

            setTimeout(() => {
                const hit = flatSelectableItems.find((item) => item.id === keyString);
                if (hit) {
                    navigate(hit.url);
                }
            }, 0);
        },
        [flatSelectableItems, navigate, onClose],
    );

    const handleRemoveFilter = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            setSelectedEntity(null);
            setQuery(query.replace(/^>\w+\s*/, "").trim());
        },
        [query, setSelectedEntity],
    );

    const handleEscape = useCallback(() => {
        setQuery("");
        setSelectedEntity(null);
        if (inputRef.current) {
            inputRef.current.blur();
        }
        setTimeout(() => {
            const activeElement = document.activeElement as HTMLElement;
            if (activeElement) {
                if (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA") {
                    activeElement.blur();
                }
                activeElement.blur();
            }
            if (document.activeElement && document.activeElement !== document.body) {
                (document.activeElement as HTMLElement).blur();
            }
        }, 0);
        onClose();
    }, [onClose, setSelectedEntity]);

    const hasResults = flatSelectableItems.length > 0;
    const hasQuery = query.trim().length > 0;

    return (
        <UntitledModalOverlay
            isOpen={isOpen}
            onOpenChange={(open) => {
                if (!open) {
                    setQuery("");
                    setSelectedEntity(null);
                    if (inputRef.current) {
                        inputRef.current.blur();
                    }
                    setTimeout(() => {
                        const activeElement = document.activeElement as HTMLElement;
                        if (activeElement) {
                            if (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA") {
                                activeElement.blur();
                            }
                            activeElement.blur();
                        }
                        if (document.activeElement && document.activeElement !== document.body) {
                            (document.activeElement as HTMLElement).blur();
                        }
                    }, 0);
                    onClose();
                }
            }}
            isDismissable
        >
            <UntitledModal className="!w-auto min-w-[33vw] max-w-2xl">
                <UntitledDialog className="outline-hidden" aria-labelledby="command-menu-title">
                    <section className="relative flex min-h-0 flex-col outline-hidden w-full max-w-2xl">
                        <div className="react-aria-TextField" data-rac="">
                            <div
                                role="presentation"
                                className="flex items-center gap-x-2 rounded-xl bg-primary p-4 relative outline-hidden after:absolute after:bottom-0 after:left-0 after:h-px after:w-full"
                            >
                                <SearchLg className="pointer-events-none absolute left-4 size-5 text-fg-quaternary" aria-hidden="true" />
                                <Input
                                    ref={inputRef}
                                    placeholder={t("commandMenu.searchPlaceholder")}
                                    value={query}
                                    onChange={setQuery}
                                    onKeyDown={(e) => {
                                        if (e.key === "Escape") {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (e.nativeEvent instanceof KeyboardEvent && e.nativeEvent.stopImmediatePropagation) {
                                                e.nativeEvent.stopImmediatePropagation();
                                            }
                                            handleEscape();
                                        } else if (e.key === "Enter") {
                                            e.preventDefault();
                                            const focusedItem = listBoxRef.current?.querySelector('[role="option"]:focus') as HTMLElement;
                                            const selectedItem = listBoxRef.current?.querySelector(
                                                '[role="option"][aria-selected="true"]',
                                            ) as HTMLElement;
                                            const targetItem = focusedItem || selectedItem;

                                            if (targetItem) {
                                                targetItem.click();
                                            } else if (allSelectableItems.current.length > 0) {
                                                handleSelection(allSelectableItems.current[0].id);
                                            }
                                        }
                                    }}
                                    className="w-full pl-11"
                                    shortcut={false}
                                    size="sm"
                                    icon={undefined}
                                />
                                <div className="flex items-center gap-x-1">
                                    {selectedEntity ? (
                                        <BadgeWithButton
                                            type="pill-color"
                                            color="brand"
                                            size="sm"
                                            onButtonClick={handleRemoveFilter}
                                            buttonLabel={t("commandMenu.removeFilter")}
                                        >
                                            {entityLabels[selectedEntity]}
                                        </BadgeWithButton>
                                    ) : (
                                        <div className="min-w-6 rounded-[4px] bg-secondary_alt px-1 py-0.5 text-center text-sm font-medium text-tertiary ring-1 ring-secondary ring-inset">
                                            ⌘K
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <h2 id="command-menu-title" slot="title" className="sr-only">
                            {t("commandMenu.title")}
                        </h2>

                        <div className="min-h-0 flex-1" ref={listBoxRef}>
                            <div className="flex max-h-[500px] flex-1 scroll-py-10 flex-col gap-2 overflow-auto focus:outline-hidden pt-2 bg-primary rounded-lg mx-2 mb-2">
                                {hasQuery ? (
                                    isLoading ? (
                                        <div className="py-12 px-4 text-center">
                                            <p className="text-sm text-tertiary">{t("commandMenu.searching", "Searching…")}</p>
                                        </div>
                                    ) : hasResults ? (
                                        groups.map((group) => (
                                            <section
                                                key={group.key}
                                                className="border-b border-secondary pb-2 last:border-transparent"
                                                role="group"
                                                aria-label={sectionHeading(group.key)}
                                            >
                                                <div className="px-3 py-1.5">
                                                    <h3 className="text-xs font-semibold text-tertiary uppercase tracking-wide">
                                                        {sectionHeading(group.key)}
                                                    </h3>
                                                </div>
                                                <ListBox
                                                    items={group.items}
                                                    onAction={handleSelection}
                                                    selectionMode="single"
                                                    className="outline-hidden"
                                                    shouldFocusOnHover={false}
                                                >
                                                    {(item: CommandPaletteItem) => (
                                                        <CommandMenuItem item={item} inputRef={inputRef} listBoxRef={listBoxRef} />
                                                    )}
                                                </ListBox>
                                            </section>
                                        ))
                                    ) : (
                                        <div className="py-16 px-4 text-center">
                                            <p className="text-sm text-tertiary">{t("commandMenu.noResults")}</p>
                                        </div>
                                    )
                                ) : (
                                    <div className="py-4 px-4">
                                        <div className="mb-3">
                                            <p className="text-sm font-medium text-secondary mb-2">
                                                {t("commandMenu.availableShortcuts", "Available shortcuts:")}
                                            </p>
                                        </div>
                                        <div className="space-y-2">
                                            {getAvailableShortcuts(userType).map((shortcut) => (
                                                <div
                                                    key={shortcut.shortcut}
                                                    className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-primary_hover cursor-pointer transition-colors"
                                                    onClick={() => {
                                                        setQuery(shortcut.shortcut + " ");
                                                        setTimeout(() => {
                                                            inputRef.current?.focus();
                                                        }, 0);
                                                    }}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <code className="px-2 py-1 bg-tertiary text-primary font-mono text-sm rounded">
                                                            {shortcut.shortcut}
                                                        </code>
                                                        <span className="text-sm text-primary">{shortcut.description}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="mt-4 pt-3 border-t border-secondary">
                                            <p className="text-xs text-tertiary text-center">
                                                {t("commandMenu.startTypingHint", "Start typing to search or use shortcuts above")}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>
                </UntitledDialog>
            </UntitledModal>
        </UntitledModalOverlay>
    );
};
